// Email routing utility for test and production environments
import { getEmailBkup, getYardiPropIdFromSlug } from './propertyMapUtil';
import UTM_TRACKING_EMAILS from '../data/utmTrackingEmails.json';
import UTM_TRACKING_EMAILS_TEST from '../data/utmTrackingEmails.test.json';

// Test email configuration
const TEST_EMAIL = 'jvtestagent@gmail.com';
const IS_DEVELOPMENT = process.env.NODE_ENV === 'development' || 
                      process.env.GATSBY_ENV === 'development' ||
                      (typeof window !== 'undefined' && (
                        window.location.hostname === 'localhost' ||
                        window.location.hostname.includes('netlify.app') ||
                        window.location.hostname.includes('preview')
                      ));

// Debug logging to see environment detection
if (typeof window !== 'undefined') {
  console.log('🔧 Email Routing Debug:', {
    NODE_ENV: process.env.NODE_ENV,
    GATSBY_ENV: process.env.GATSBY_ENV,
    hostname: window.location.hostname,
    IS_DEVELOPMENT
  });
}

// UTM Source mapping - maps incoming UTM sources to tracking email keys
const UTM_SOURCE_MAPPING = {
  'gmb': 'google my business',
  'google-my-business': 'google my business',
  'googlemybusiness': 'google my business',
  'google_my_business': 'google my business',
  'gmbupdates': 'google my business',
  'google-ads': 'google ads',
  'googleads': 'google ads',
  'google_ads': 'google ads',
  'ppc': 'google ads',
  'google': 'google',
  'apartments': 'apartments.com',
  'apartmentsdotcom': 'apartments.com',
  'apartments_com': 'apartments.com',
  'rent': 'rent',
  'rentdotcom': 'rent.com',
  'rent_com': 'rent.com',
  'realtor': 'realtor.com',
  'realtordotcom': 'realtor.com',
  'realtor_com': 'realtor.com',
  'fb': 'facebook',
  'meta': 'facebook',
  'instagram': 'facebook',
  'tiktok': 'tik tok',
  'website': 'property website',
  'direct': 'property website',
  'organic': 'property website',
  'referral': 'referral - cross sell',
  'cross-sell': 'referral - cross sell',
  'crosssell': 'referral - cross sell',
  'cross_sell': 'referral - cross sell',
  '407apartments': '407apartments',
  '407_apartments': '407apartments',
  'bing.com': 'bing',
  'bing': 'bing',
  'bing_com': 'bing',
  'bing_dot_com': 'bing',
  'bing_dot_com': 'bing',
  'tiktok.com': 'tik tok',
  'tiktok': 'tik tok',
  'tiktok_com': 'tik tok',
  'tiktok_dot_com': 'tik tok',
  'tiktok_dot_com': 'tik tok',
  'chatgpt.com': 'chatgpt.com',
  'chatgpt': 'chatgpt.com',
  'chatgpt_com': 'chatgpt.com',
  'chatgpt_dot_com': 'chatgpt.com',
  'chatgpt_dot_com': 'chatgpt.com',
};

/**
 * Get UTM-based tracking email if available
 * @param {string} slug - Property slug
 * @param {string} utmSource - UTM source parameter
 * @returns {string|null} - Tracking email or null if not found
 */
const getUTMTrackingEmail = (slug, utmSource) => {
  if (!slug || !utmSource) return null;
  
  // Convert community slug to Yardi property code
  const yardiCode = getYardiPropIdFromSlug(slug);
  if (!yardiCode) return null;
  
  // Normalize and map UTM source to tracking email key
  const normalizedUtmSource = utmSource.toLowerCase().trim();
  const mappedSource = UTM_SOURCE_MAPPING[normalizedUtmSource] || normalizedUtmSource;
  
  // Use test emails in development, production emails in production
  const emailConfig = IS_DEVELOPMENT ? UTM_TRACKING_EMAILS_TEST : UTM_TRACKING_EMAILS;
  const propertyMapping = emailConfig[yardiCode];
  if (!propertyMapping) return null;
  
  return propertyMapping[mappedSource] || null;
};

/**
 * Get the appropriate email recipient based on environment and UTM parameters
 * In development/test: uses test UTM tracking emails or fallback to jvuong@mckinley.com
 * In production: checks for UTM tracking email first, then uses original toEmail parameter
 */
export const getEmailRecipient = (toEmail, slug = null, utmSource = null) => {
  // Check for UTM tracking email first (works for both dev and production)
  if (slug && utmSource) {
    const trackingEmail = getUTMTrackingEmail(slug, utmSource);
    if (trackingEmail) {
      return trackingEmail;
    }
  }
  
  // Development fallback: use TEST_EMAIL if no UTM tracking email found
  if (IS_DEVELOPMENT) {
    return TEST_EMAIL;
  }
  
  // Production fallback: use provided email or jvuong@mckinley.com for testing
  return toEmail || 'jvuong@mckinley.com';
};

/**
 * Get the CC email recipient based on environment
 * In development/test: no CC to avoid spam
 * In production: uses the property-specific backup email and jvuong@mckinley.com
 */
export const getCCEmailRecipient = (slug = null) => {
  if (IS_DEVELOPMENT) {
    return null; // No CC in development
  }
  
  // Production: use property-specific backup email + jvuong
  const backupEmail = getEmailBkup(slug);
  if (backupEmail) {
    return `${backupEmail}, jvuong@mckinley.com`;
  }
  return 'jvuong@mckinley.com';
};

/**
 * Get vendor form email recipient
 */
export const getVendorEmailRecipient = (toEmail = null) => {
  if (IS_DEVELOPMENT) {
    return TEST_EMAIL;
  }
  
  return toEmail || 'vendors@mckinley.com';
};

/**
 * Add environment info to email body for tracking
 */
export const addEnvironmentInfo = (body, additionalInfo = {}) => {
  const environmentInfo = `
-------------------------------
Environment: ${IS_DEVELOPMENT ? 'Development/Test' : 'Production'}
Timestamp: ${new Date().toISOString()}
${additionalInfo.utmSource ? `UTM Source: ${additionalInfo.utmSource}` : 'UTM Source: McKinley.com'}
${additionalInfo.utmMedium ? `UTM Medium: ${additionalInfo.utmMedium}` : 'UTM Medium: N/A'}
${additionalInfo.utmCampaign ? `UTM Campaign: ${additionalInfo.utmCampaign}` : 'UTM Campaign: N/A'}
${additionalInfo.referrer ? `Referrer: ${additionalInfo.referrer}` : 'Referrer: Direct'}
${additionalInfo.sessionId ? `Session ID: ${additionalInfo.sessionId}` : ''}`;

  return body + '\n\n' + environmentInfo;
};